// material-ui
import {
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import BlockIcon from '@material-ui/icons/Block';
import CheckIcon from '@material-ui/icons/Check';
import DeleteIcon from '@material-ui/icons/Delete';
import InfoIcon from '@material-ui/icons/Info';
import { Autocomplete } from '@material-ui/lab';
import { cloneDeep } from 'lodash-es';
import React, { useState } from 'react';

import {
  makeTargetingChangeDescription,
  makeTargetingChangeInfoHover,
  TargetingLogic,
  TargetingLogicOR,
} from '~/src/dash/api/console-api';
import ConfirmationDialogButton from '~/src/dash/components/confirmation-dialog-button';
import {
  makeInventoryRouteTargetingChangeDescription,
  makeInventoryRouteTargetingChangeInfoHover,
} from '~/src/dash/components/input-row';
import { MaterialSearchDropdownMulti } from '~/src/dash/components/material-search-dropdown-multi';
import { SortByMode } from '~/src/dash/components/material-search-dropdown-multi-list';
import { ModelSecondaryButton } from '~/src/dash/components/model-secondary-button';
import { Textarea } from '~/src/dash/publicaUI';
import { IMenuList } from '~/src/dash/types/menuList';
import { CloneType, IBulkUpdateItem } from '~/src/dash/utils/bulk-update';
import { publicaColors } from '~/src/dash/utils/colors';
import {
  generateCitiesForCountryMenuList,
  generateCitiesForStateMenuList,
  generateCountriesMenuList,
  generateDMAsMenu,
  generateStatesMenuList,
} from '~/src/dash/utils/countries';
import { allSupportedStates, ICountryState, stateNamesToStateList } from '~/src/dash/utils/states';
import { USCities } from '~/src/dash/utils/us-cities';

interface IProps {
  // local targeting fields
  LocalTargetingIsAllowed: boolean;
  localTargetingParameters: ILocalTargetingParameter[];
  LocalTargetingLogic: TargetingLogic;
  localTargetingLogicFieldName: string;
  disabled: boolean;
  resourceName: string;
  titleOverride?: string;

  // optional props for bulk / cloning
  isCloneOrBulkUpdate?: boolean;
  checkIfRowIsMarked?: (fields: string[]) => boolean;
  handleChangeMarked?: (bulkUpdateItems: IBulkUpdateItem[]) => void;
  getCloneType?: (key: string) => CloneType;

  // handler functions
  addLocalTargetingParam: () => void;
  handleChange: ({ target }: React.ChangeEvent<HTMLInputElement>) => void;
  updateLocalTargetingParam: (index: number, updates: object) => void;
  deleteLocalTargetingParam: (index: number) => void;

  // used for destination / routes where we have multiple rows of local targeting
  enableMultipleLocalTargeting?: boolean;
  multipleLocalTargetingIndex?: number;
}

const useStyles = makeStyles({
  checkbox: {
    transform: 'scale(0.8)',
    padding: 3,
  },
  insideAccordionText: {
    fontSize: 14,
    fontWeight: 400,
    color: '$434343',
  },
  insideAccordionTextBold: {
    fontSize: 14,
    fontWeight: 500,
    color: '$434343',
  },
  standardFont: {
    fontSize: 14,
  },
  localTargetingBox: {
    width: '97%',
    border: '1px solid grey',
    borderRadius: 4,
    marginTop: 10,
    marginBottom: 10,
  },
  negativeMargin: {
    marginTop: -5,
  },
  smallNegativeMargin: {
    marginTop: -3,
  },
  boldFont: {
    fontSize: 14,
    fontWeight: 500,
  },
});

// ILocalTargetingParameter represents targeting parameters (City, ZipCode, DMA ... etc)
export interface ILocalTargetingParameter {
  type: string; // city, state, zip, dma (all v1) and country (v2)
  value: string; // holds values for v1
  currentValue: string; // holds current values for v1
  isInputInCommaSeparatedMode: boolean;
  // these all hold the specific values for country type params (v2)
  country: string; // single value, representing country for entire object, mandatory
  states: string; // comma separated, optional
  areStatesCommaSeparated: boolean;
  cities: string; // comma separated, optional
  areCitiesCommaSeparated: boolean;
  zips: string; // comma separated, optional
  dmas: string; // comma separated, optional
  areDMAsCommaSeparated: boolean;
}

export type LocalParamType = string;
export const localParamState: LocalParamType = 'state';
export const localParamCity: LocalParamType = 'city';
export const localParamZip: LocalParamType = 'zip';
export const localParamDMA: LocalParamType = 'dma';
export const localParamCountry: LocalParamType = 'country';

export const includedTitleOverride = 'Local Targeting Included';
export const excludedTitleOverride = 'Local Targeting Excluded';

export const LocalTargeting = (props: IProps) => {
  const {
    isCloneOrBulkUpdate,
    checkIfRowIsMarked,
    handleChangeMarked,
    localTargetingParameters,
    LocalTargetingLogic,
    localTargetingLogicFieldName,
    LocalTargetingIsAllowed,
    addLocalTargetingParam,
    disabled,
    handleChange,
    updateLocalTargetingParam,
    getCloneType,
    deleteLocalTargetingParam,
    titleOverride,
    enableMultipleLocalTargeting,
    multipleLocalTargetingIndex,
    resourceName,
  } = props;

  const classes = useStyles();
  const countriesList = useState(generateCountriesMenuList())[0]; // don't need to set state

  const handleChangeList = (
    localParam: ILocalTargetingParameter,
    localParamIndex: number,
    list: IMenuList,
    type: LocalParamType,
  ) => {
    const selectedValues = [] as string[];
    for (const item of Object.values(list)) {
      if (item.selected) {
        selectedValues.push(item.ID);
      }
    }

    switch (type) {
      // set the new value and delete old dependent values
      case localParamCountry:
        localParam.country = selectedValues.join(',');
        localParam.states = '';
        localParam.cities = '';
        localParam.zips = '';
        localParam.dmas = '';
        break;
      case localParamState:
        localParam.states = selectedValues.join(',');
        localParam.cities = '';
        break;
      case localParamCity:
        localParam.cities = selectedValues.join(',');
        break;
      case localParamDMA:
        localParam.dmas = selectedValues.join(',');
        break;
      default:
        break;
    }

    updateLocalTargetingParam(localParamIndex, localParam);
  };

  const handleChangeLocalTargetingParam = (
    localParam: ILocalTargetingParameter,
    localParamIndex: number,
    value: string,
    type: LocalParamType,
  ) => {
    switch (type) {
      case localParamState:
        localParam.states = value;
        // if more than 1 state is selected, we delete the cities value
        if (value.split(',').length > 1) {
          localParam.cities = '';
        }
        break;
      case localParamCity:
        localParam.cities = value;
        break;
      case localParamZip:
        localParam.zips = value;
        break;
      case localParamDMA:
        localParam.dmas = value;
        break;
      default:
        break;
    }
    updateLocalTargetingParam(localParamIndex, localParam);
  };

  const renderIncludeExclude = () => {
    if (titleOverride === includedTitleOverride) {
      return (
        <Grid item xs={1} className={classes.negativeMargin}>
          <IconButton disableRipple={true}>
            <Tooltip title={includedTitleOverride}>
              <CheckIcon style={{ color: publicaColors.green }} />
            </Tooltip>
          </IconButton>
        </Grid>
      );
    } else if (titleOverride === excludedTitleOverride) {
      return (
        <Grid item xs={1} className={classes.negativeMargin}>
          <IconButton disableRipple={true}>
            <Tooltip title={excludedTitleOverride}>
              <BlockIcon style={{ color: publicaColors.red }} />
            </Tooltip>
          </IconButton>
        </Grid>
      );
    } else {
      return (
        <Grid item xs={1} className={classes.negativeMargin}>
          <ConfirmationDialogButton
            title='Targeting Change'
            description={
              enableMultipleLocalTargeting
                ? makeInventoryRouteTargetingChangeDescription(LocalTargetingIsAllowed)
                : makeTargetingChangeDescription(LocalTargetingIsAllowed)
            }
            triggerComponentInfo={
              enableMultipleLocalTargeting
                ? makeInventoryRouteTargetingChangeInfoHover(LocalTargetingIsAllowed)
                : makeTargetingChangeInfoHover(LocalTargetingIsAllowed)
            }
            acceptButtonTitle='Confirm'
            acceptButtonCallBack={() =>
              handleChange({
                target: {
                  name: 'LocalTargetingIsAllowed',
                  value: enableMultipleLocalTargeting
                    ? multipleLocalTargetingIndex
                    : (!LocalTargetingIsAllowed as any),
                },
              } as any)
            }
            disabled={disabled}
            refuseButtonTitle='Cancel'
          >
            <IconButton>
              {LocalTargetingIsAllowed ? (
                <CheckIcon style={{ color: publicaColors.green }} />
              ) : (
                <BlockIcon style={{ color: publicaColors.red }} />
              )}
            </IconButton>
          </ConfirmationDialogButton>
        </Grid>
      );
    }
  };

  const renderAndOr = (index: number) => {
    return (
      <Grid
        item
        xs={1}
        className={classes.smallNegativeMargin}
        onClick={() =>
          handleChange({
            target: {
              name: localTargetingLogicFieldName,
              value: index,
            },
          } as any)
        }
      >
        <Tooltip
          title={
            LocalTargetingLogic === TargetingLogicOR
              ? `Requests will reach this ${resourceName} if any of the defined locations match`
              : `Requests will reach this ${resourceName} only if all of the defined locations match`
          }
        >
          <IconButton disableRipple={true}>
            <Typography className={classes.boldFont}>
              {LocalTargetingLogic === TargetingLogicOR ? 'OR' : 'AND'}
            </Typography>
          </IconButton>
        </Tooltip>
      </Grid>
    );
  };

  const renderNewLocalTargetingParam = (
    localParam: ILocalTargetingParameter,
    localParamIndex: number,
  ) => {
    const localParamCountryList = cloneDeep(countriesList);
    if (localParam.country !== '' && localParamCountryList[localParam.country]) {
      localParamCountryList[localParam.country].selected = true;
    }
    const localParamStatesList = generateStatesMenuList(
      localParam.country,
      localParam.states.split(','),
    );
    const localParamDMAs = generateDMAsMenu(localParam.country, localParam.dmas.split(','));

    // we override the form to be comma separated and not a list if there are no options available in the dropdown
    let statesInputInCommaSeparatedModeOverride = false;
    let citiesInputInCommaSeparatedModeOverride = false;
    const countrySelected = localParam.country !== '';
    const selectedStatesCount = localParam.states
      .split(',')
      .filter((item) => item.trim().length).length;
    const totalStatesCount = Object.values(localParamStatesList).length;
    if (totalStatesCount === 0) {
      statesInputInCommaSeparatedModeOverride = true;
    }

    // cities row should be active if
    // 1. country is selected but no states are available
    // 2. single state and country are selected
    let enableCitiesRow = false;
    let localParamCitiesList = {} as IMenuList;
    if (countrySelected && totalStatesCount === 0) {
      enableCitiesRow = true;
      localParamCitiesList = generateCitiesForCountryMenuList(
        localParam.country,
        localParam.cities.split(','),
      );
      const totalCitiesCount = Object.values(localParamCitiesList).length;
      if (totalCitiesCount === 0) {
        citiesInputInCommaSeparatedModeOverride = true;
      }
    } else if (countrySelected && selectedStatesCount === 1) {
      enableCitiesRow = true;
      localParamCitiesList = generateCitiesForStateMenuList(
        localParam.country,
        localParam.states,
        localParam.cities.split(','),
      );
      const totalCitiesCount = Object.values(localParamCitiesList).length;
      if (totalCitiesCount === 0) {
        citiesInputInCommaSeparatedModeOverride = true;
      }
    }

    return (
      <Grid item className={classes.localTargetingBox}>
        <Grid container direction='column' alignItems='flex-start' spacing={1}>
          {/* Country Row */}
          <Grid item style={{ width: '100%' }}>
            <Grid container direction='row' alignItems='center'>
              <Grid item xs={2}>
                <Typography className={classes.standardFont}>Country</Typography>
              </Grid>
              <Grid item xs={9}>
                <MaterialSearchDropdownMulti
                  fontSize='14px'
                  fontColor={publicaColors.black}
                  menuTitle={localParamCountry}
                  menuTitleOverride={localParam.country === '' ? 'None' : ''}
                  selectAll={false}
                  enableMultiSelect={false}
                  list={localParamCountryList}
                  disabled={disabled}
                  onChange={(list: IMenuList) =>
                    handleChangeList(localParam, localParamIndex, list, localParamCountry)
                  }
                  handleMenuAcceptClick={(list: IMenuList) =>
                    handleChangeList(localParam, localParamIndex, list, localParamCountry)
                  }
                  overrideDefaultSortMode={SortByMode.AtoZ}
                />
              </Grid>
              {/* Delete Button */}
              <Grid item xs={1}>
                <IconButton
                  disabled={disabled}
                  aria-label='delete'
                  onClick={() => {
                    deleteLocalTargetingParam(localParamIndex);
                  }}
                >
                  <DeleteIcon style={{ color: 'grey' }} />
                </IconButton>
              </Grid>
            </Grid>
          </Grid>
          {/* Zip Row */}
          {countrySelected && (
            <Grid item style={{ width: '100%' }}>
              <Grid container direction='row' alignItems='center' spacing={1}>
                <Grid item xs={2}>
                  <Typography className={classes.standardFont}>ZIPs</Typography>
                </Grid>
                <Grid item xs={9}>
                  <Textarea
                    fullWidth
                    msDelay={800}
                    isDisabled={disabled}
                    placeholder='Values can be comma-separated (Ex: 90800, 90801)'
                    initialValue={localParam.zips}
                    handleOnChange={(value: string) => {
                      handleChangeLocalTargetingParam(
                        localParam,
                        localParamIndex,
                        value,
                        localParamZip,
                      );
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          )}
          {/* dma Row (ONLY IN US) */}
          {localParam.country === 'us' && (
            <Grid item style={{ width: '100%' }}>
              <Grid container direction='row' alignItems='center' spacing={1}>
                <Grid item xs={2}>
                  <Typography className={classes.standardFont}>DMAs</Typography>
                </Grid>
                <Grid item xs={5}>
                  {localParam.areDMAsCommaSeparated ? (
                    <Textarea
                      fullWidth
                      msDelay={800}
                      isDisabled={disabled}
                      placeholder='Values can be comma-separated (Ex: 508, 509)'
                      initialValue={localParam.dmas}
                      handleOnChange={(value: string) => {
                        handleChangeLocalTargetingParam(
                          localParam,
                          localParamIndex,
                          value,
                          localParamDMA,
                        );
                      }}
                    />
                  ) : (
                    <MaterialSearchDropdownMulti
                      fontSize='14px'
                      fontColor={publicaColors.black}
                      menuTitle='DMAs'
                      menuTitleOverride={localParam.dmas === '' ? 'None' : ''}
                      selectAll={true}
                      enableMultiSelect={true}
                      list={localParamDMAs}
                      disabled={disabled}
                      onChange={(list: IMenuList) => {
                        handleChangeList(localParam, localParamIndex, list, localParamDMA);
                      }}
                      handleMenuAcceptClick={(list: IMenuList) =>
                        handleChangeList(localParam, localParamIndex, list, localParamDMA)
                      }
                      overrideDefaultSortMode={SortByMode.AtoZ}
                    />
                  )}
                </Grid>
                <Grid item xs={4}>
                  <FormControlLabel
                    label={
                      <Typography className={classes.standardFont}>Comma separated</Typography>
                    }
                    control={
                      <Checkbox
                        disabled={disabled}
                        checked={localParam.areDMAsCommaSeparated}
                        className={localParamDMA}
                        onChange={() => {
                          localParam.areDMAsCommaSeparated = !localParam.areDMAsCommaSeparated;
                          updateLocalTargetingParam(localParamIndex, localParam);
                        }}
                        color='primary'
                      />
                    }
                  />
                </Grid>
              </Grid>
            </Grid>
          )}
          {/* State Row */}
          {countrySelected && (
            <Grid item style={{ width: '100%' }}>
              <Grid container direction='row' alignItems='center' spacing={1}>
                <Grid item xs={2}>
                  <Typography className={classes.standardFont}>State(s)</Typography>
                </Grid>
                <Grid item xs={5}>
                  {localParam.areStatesCommaSeparated || statesInputInCommaSeparatedModeOverride ? (
                    <Textarea
                      fullWidth
                      msDelay={800}
                      isDisabled={disabled}
                      placeholder='Values can be comma-separated (Ex: California, Utah)'
                      initialValue={localParam.states}
                      handleOnChange={(value: string) => {
                        handleChangeLocalTargetingParam(
                          localParam,
                          localParamIndex,
                          value,
                          localParamState,
                        );
                      }}
                    />
                  ) : (
                    <MaterialSearchDropdownMulti
                      fontSize='14px'
                      fontColor={publicaColors.black}
                      menuTitle='States'
                      menuTitleOverride={localParam.states === '' ? 'None' : ''}
                      selectAll={true}
                      enableMultiSelect={true}
                      list={localParamStatesList}
                      disabled={disabled}
                      onChange={(list: IMenuList) =>
                        handleChangeList(localParam, localParamIndex, list, localParamState)
                      }
                      handleMenuAcceptClick={(list: IMenuList) =>
                        handleChangeList(localParam, localParamIndex, list, localParamState)
                      }
                      overrideDefaultSortMode={SortByMode.AtoZ}
                    />
                  )}
                </Grid>
                <Tooltip
                  title={
                    statesInputInCommaSeparatedModeOverride
                      ? 'No states available for selection'
                      : ''
                  }
                >
                  <Grid item xs={4}>
                    <FormControlLabel
                      label={
                        <Typography className={classes.standardFont}>Comma separated</Typography>
                      }
                      control={
                        <Checkbox
                          disabled={disabled || statesInputInCommaSeparatedModeOverride}
                          checked={
                            localParam.areStatesCommaSeparated ||
                            statesInputInCommaSeparatedModeOverride
                          }
                          className={localParamState}
                          onChange={() => {
                            localParam.areStatesCommaSeparated =
                              !localParam.areStatesCommaSeparated;
                            updateLocalTargetingParam(localParamIndex, localParam);
                          }}
                          color='primary'
                        />
                      }
                    />
                  </Grid>
                </Tooltip>
              </Grid>
            </Grid>
          )}
          {/* Cities Row */}
          <Tooltip
            title={
              enableCitiesRow
                ? ''
                : 'Select a single State if available to ensure city targeting accuracy'
            }
          >
            <Grid item style={{ width: '100%' }}>
              <Grid container direction='row' alignItems='center' spacing={1}>
                <Grid item xs={2}>
                  <Typography className={classes.standardFont}>Cities</Typography>
                </Grid>
                <Grid item xs={5}>
                  {localParam.areCitiesCommaSeparated || citiesInputInCommaSeparatedModeOverride ? (
                    <Textarea
                      fullWidth
                      msDelay={800}
                      isDisabled={disabled || !enableCitiesRow}
                      placeholder='Values can be comma - separated(Ex: San Francisco, Los Angeles)'
                      initialValue={localParam.cities}
                      handleOnChange={(value: string) => {
                        handleChangeLocalTargetingParam(
                          localParam,
                          localParamIndex,
                          value,
                          localParamCity,
                        );
                      }}
                    />
                  ) : (
                    <MaterialSearchDropdownMulti
                      fontSize='14px'
                      fontColor={publicaColors.black}
                      menuTitle='Cities'
                      menuTitleOverride={localParam.cities === '' ? 'None' : ''}
                      selectAll={true}
                      enableMultiSelect={true}
                      list={localParamCitiesList}
                      disabled={disabled || !enableCitiesRow}
                      onChange={(list: IMenuList) =>
                        handleChangeList(localParam, localParamIndex, list, localParamCity)
                      }
                      handleMenuAcceptClick={(list: IMenuList) =>
                        handleChangeList(localParam, localParamIndex, list, localParamCity)
                      }
                      overrideDefaultSortMode={SortByMode.AtoZ}
                    />
                  )}
                </Grid>
                <Tooltip
                  title={
                    citiesInputInCommaSeparatedModeOverride
                      ? 'No cities available for selection'
                      : ''
                  }
                >
                  <Grid item xs={4}>
                    <FormControlLabel
                      label={
                        <Typography className={classes.standardFont}>Comma separated</Typography>
                      }
                      control={
                        <Checkbox
                          disabled={
                            disabled || citiesInputInCommaSeparatedModeOverride || !enableCitiesRow
                          }
                          checked={
                            localParam.areCitiesCommaSeparated ||
                            citiesInputInCommaSeparatedModeOverride
                          }
                          className={localParamCity}
                          onChange={() => {
                            localParam.areCitiesCommaSeparated =
                              !localParam.areCitiesCommaSeparated;
                            updateLocalTargetingParam(localParamIndex, localParam);
                          }}
                          color='primary'
                        />
                      }
                    />
                  </Grid>
                </Tooltip>
              </Grid>
            </Grid>
          </Tooltip>
        </Grid>
      </Grid>
    );
  };

  const renderOldLocalTargetingParam = (
    localParam: ILocalTargetingParameter,
    localParamIndex: number,
  ) => {
    // render the v1 type
    let placeholderText = 'Values can be comma-separated (Ex: California, Utah)';
    if (localParam.type === localParamCity) {
      placeholderText = 'Values can be comma - separated(Ex: San Francisco, Los Angeles)';
    } else if (localParam.type === localParamZip) {
      placeholderText = 'Values can be comma-separated (Ex: 90800, 90801)';
    } else if (localParam.type === localParamDMA) {
      placeholderText = 'Values can be comma-separated (Ex: 508, 509)';
    }

    return (
      <Grid item className={classes.localTargetingBox}>
        <Grid container direction='column' justify='center' alignItems='flex-start' spacing={1}>
          {/* Type Selector */}
          <Grid item style={{ width: '100%' }}>
            <Grid container direction='row' alignItems='center'>
              <Grid item xs={2}>
                <Typography className={classes.standardFont}>Type</Typography>
              </Grid>
              {/* Type Select */}
              <Grid item xs={4}>
                <Select
                  value={localParam.type}
                  style={{ fontSize: 14, width: '98%' }}
                  disabled={disabled}
                  onChange={(event: any) => {
                    // If type change, delete previous value
                    if (localParam.type !== event.target.value) {
                      localParam.value = '';
                      localParam.currentValue = '';
                    }
                    updateLocalTargetingParam(localParamIndex, {
                      type: event.target.value,
                      isInputInCommaSeparatedMode:
                        localTargetingParameters[localParamIndex].isInputInCommaSeparatedMode,
                    });
                  }}
                >
                  <MenuItem value='state'>State</MenuItem>
                  <MenuItem value='city'>City</MenuItem>
                  <MenuItem value='zip'>Zip Code</MenuItem>
                  <MenuItem value='dma'>DMA Code</MenuItem>
                </Select>
              </Grid>
              {/* Comma-separated option checkbox */}
              <Grid item xs={1} />
              {localParam.type === 'city' || localParam.type === 'state' ? (
                <Grid item xs={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        disabled={disabled}
                        checked={
                          localTargetingParameters[localParamIndex].isInputInCommaSeparatedMode
                        }
                        onChange={() => {
                          updateLocalTargetingParam(localParamIndex, {
                            isInputInCommaSeparatedMode:
                              !localTargetingParameters[localParamIndex]
                                .isInputInCommaSeparatedMode,
                          });
                        }}
                        color='primary'
                      />
                    }
                    label={
                      <Typography className={classes.standardFont}>Comma separated</Typography>
                    }
                  />
                </Grid>
              ) : (
                <Grid item xs={4} />
              )}
              {/* Delete Button */}
              <Grid item xs={1}>
                <IconButton
                  disabled={disabled}
                  aria-label='delete'
                  onClick={() => {
                    deleteLocalTargetingParam(localParamIndex);
                  }}
                >
                  <DeleteIcon style={{ color: 'grey' }} />
                </IconButton>
              </Grid>
            </Grid>
          </Grid>
          {/* Value & Delete */}
          <Grid item style={{ width: '95%' }}>
            <Grid container direction='row' justify='space-between' alignItems='center'>
              <Grid item xs={11}>
                {/* Input Field, used for comma separated mode  */}
                {localParam.type === localParamZip ||
                localParam.type === localParamDMA ||
                localTargetingParameters[localParamIndex].isInputInCommaSeparatedMode ? (
                  <Textarea
                    fullWidth
                    msDelay={800}
                    isDisabled={disabled}
                    placeholder={placeholderText}
                    initialValue={localTargetingParameters[localParamIndex].value}
                    handleOnChange={(value: string) => {
                      updateLocalTargetingParam(localParamIndex, { value });
                    }}
                  />
                ) : localParam.type === localParamState ? (
                  // States autocomplete
                  <Autocomplete
                    disabled={disabled}
                    multiple
                    disableCloseOnSelect
                    options={allSupportedStates}
                    getOptionLabel={(o: ICountryState) => o.name}
                    renderOption={(option, { selected }) => (
                      <React.Fragment>
                        <Checkbox style={{ marginRight: 8 }} checked={selected} />
                        {option.name}
                      </React.Fragment>
                    )}
                    style={{ width: '100%' }}
                    renderInput={(params) => (
                      <TextField {...params} variant='outlined' label={'States'} />
                    )}
                    onChange={(_event: object, values: ICountryState[]) =>
                      updateLocalTargetingParam(localParamIndex, {
                        value: values.map((v: ICountryState) => v.name).join(','),
                        currentValue: '',
                        isInputInCommaSeparatedMode:
                          localTargetingParameters[localParamIndex].isInputInCommaSeparatedMode,
                      })
                    }
                    defaultValue={stateNamesToStateList(
                      localTargetingParameters[localParamIndex].value
                        .split(',')
                        .filter((s: string) => s.length > 0)
                        .map((o: string) => o.trim()),
                    )}
                    size='small'
                  />
                ) : (
                  // Cities autocomplete
                  <Autocomplete
                    multiple
                    disabled={disabled}
                    disableCloseOnSelect
                    defaultValue={localTargetingParameters[localParamIndex].value
                      .split(',')
                      .filter((s: string) => s.length > 0)
                      .map((o: string) => o.trim())}
                    options={
                      localTargetingParameters[localParamIndex].currentValue.length <= 2
                        ? []
                        : USCities
                    }
                    renderOption={(option, { selected }) => (
                      <React.Fragment>
                        <Checkbox style={{ marginRight: 8 }} checked={selected} />
                        {option}
                      </React.Fragment>
                    )}
                    style={{ width: '100%' }}
                    renderInput={(params) => (
                      <TextField {...params} variant='outlined' label='Cities' />
                    )}
                    onInputChange={(_event: object, value: string) =>
                      updateLocalTargetingParam(localParamIndex, {
                        currentValue: value,
                        isInputInCommaSeparatedMode:
                          localTargetingParameters[localParamIndex].isInputInCommaSeparatedMode,
                      })
                    }
                    onChange={(_event: object, values: string[]) =>
                      updateLocalTargetingParam(localParamIndex, {
                        value: values.join(','),
                        currentValue: '',
                        isInputInCommaSeparatedMode:
                          localTargetingParameters[localParamIndex].isInputInCommaSeparatedMode,
                      })
                    }
                  />
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  return (
    <Grid item style={{ width: '100%', marginBottom: 25 }}>
      <Grid container direction='row' justify='flex-start' alignItems='flex-start' spacing={0}>
        <Grid item xs={3}>
          <Grid container direction='row' justify='flex-start' alignItems='center' spacing={0}>
            {isCloneOrBulkUpdate && checkIfRowIsMarked && handleChangeMarked && getCloneType && (
              <Grid item>
                <Checkbox
                  className={classes.checkbox}
                  checked={checkIfRowIsMarked(['LocalTargeting', 'LocalTargetingIsAllowed'])}
                  onChange={() =>
                    handleChangeMarked([
                      {
                        key: 'LocalTargeting',
                        displayTitle: 'Local Targeting',
                        genDisplayValue: () => localTargetingParameters.length + ' Local Targets',
                        enabled: false,
                        genType: () => getCloneType('LocalTargeting'),
                        defaultValue: '',
                        tab: 'Targeting',
                      },
                      {
                        key: 'LocalTargetingIsAllowed',
                        displayTitle: 'Local Targeting Allowed',
                        genDisplayValue: () => (LocalTargetingIsAllowed ? 'Allowed' : 'Denied'),
                        enabled: false,
                        genType: () => getCloneType('LocalTargetingIsAllowed'),
                        defaultValue: true,
                        tab: 'Targeting',
                      },
                    ])
                  }
                />
              </Grid>
            )}
            <Grid item>
              <Typography
                className={
                  localTargetingParameters.length
                    ? classes.insideAccordionTextBold
                    : classes.insideAccordionText
                }
              >
                {titleOverride || 'Local Targeting'}
                <Tooltip
                  title='Combine any layer of geo targeting to granularly define the location where this bidder should be targeted to. Targeting will be enforced looking at all params set per location. Add multiple locations and combine them using the AND / OR logic to achieve desired targeting.'
                  placement='bottom'
                >
                  <InfoIcon style={{ width: '1rem', color: publicaColors.mediumGray }} />
                </Tooltip>
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        {renderAndOr(multipleLocalTargetingIndex || 0)}
        {renderIncludeExclude()}
        <Grid item xs={7}>
          <Grid container direction='column' justify='center' alignItems='flex-start' spacing={2}>
            {localTargetingParameters.map(
              (localParam: ILocalTargetingParameter, localParamIndex: number) => {
                if (['state', 'city', 'zip', 'dma'].includes(localParam.type)) {
                  return renderOldLocalTargetingParam(localParam, localParamIndex);
                } else {
                  return renderNewLocalTargetingParam(localParam, localParamIndex);
                }
              },
            )}
            {/* 'Add New' button */}
            <Grid item>
              <ModelSecondaryButton
                disabled={disabled}
                style={{
                  paddingTop: '9px',
                  paddingBottom: '9px',
                }}
                onClick={addLocalTargetingParam}
              >
                <Typography
                  noWrap
                  style={{
                    textTransform: 'uppercase',
                    fontSize: 14,
                    fontWeight: 500,
                    marginLeft: -12,
                  }}
                >
                  + Add Location
                </Typography>
              </ModelSecondaryButton>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export const validateLocalTargetingRule = (
  localTargetingAllowedParams: ILocalTargetingParameter[],
  localTargetingDisallowedParams: ILocalTargetingParameter[],
): string[] => {
  const errors = [
    ...validateLocalTargeting(localTargetingAllowedParams),
    ...validateLocalTargeting(localTargetingDisallowedParams),
  ];

  // lt = "Local Targeting"
  for (const ltAllowParams of localTargetingAllowedParams) {
    const allowedStates = ltAllowParams.states.split(',');
    const allowedCities = ltAllowParams.cities.split(',');
    const allowedZips = ltAllowParams.zips.split(',');
    const allowedDMAs = ltAllowParams.dmas.split(',');

    for (const ltDisallowParams of localTargetingDisallowedParams) {
      if (ltAllowParams.country !== ltDisallowParams.country) {
        continue;
      }
      for (const overlapState of ltDisallowParams.states
        .split(',')
        .filter((state) => state && allowedStates.includes(state))) {
        errors.push(
          `You cannot both include and exclude the same local target, type 'State': ${overlapState}`,
        );
      }
      for (const overlapCity of ltDisallowParams.cities
        .split(',')
        .filter((city) => city && allowedCities.includes(city))) {
        errors.push(
          `You cannot both include and exclude the same local target, type 'City': ${overlapCity}`,
        );
      }
      for (const overlapZip of ltDisallowParams.zips
        .split(',')
        .filter((zip) => zip && allowedZips.includes(zip))) {
        errors.push(
          `You cannot both include and exclude the same local target, type 'Zip code': ${overlapZip}`,
        );
      }
      for (const overlapDMA of ltDisallowParams.dmas
        .split(',')
        .filter((dma) => dma && allowedDMAs.includes(dma))) {
        errors.push(
          `You cannot both include and exclude the same local target, type 'DMA': ${overlapDMA}`,
        );
      }
    }
  }

  return errors;
};

export const validateLocalTargeting = (
  localTargetingParams: ILocalTargetingParameter[],
): string[] => {
  const stateValsMap = {} as { [key: string]: boolean };
  const cityValsMap = {} as { [key: string]: boolean };
  const zipValsMap = {} as { [key: string]: boolean };
  const dmaValsMap = {} as { [key: string]: boolean };

  const errors = [] as string[];

  for (const localTargets of localTargetingParams) {
    if (localTargets.states.length > 0) {
      for (const state of localTargets.states.split(',')) {
        if (stateValsMap[state]) {
          errors.push(`Duplicate value for Local Panel, Type 'State': ${state}`);
        } else {
          stateValsMap[state] = true;
        }
      }
    }
    if (localTargets.cities.length > 0) {
      for (const city of localTargets.cities.split(',')) {
        if (cityValsMap[city]) {
          errors.push(`Duplicate value for Local Panel, Type 'City': ${city}`);
        } else {
          cityValsMap[city] = true;
        }
      }
    }
    if (localTargets.zips.length > 0) {
      for (const zip of localTargets.zips.split(',')) {
        if (zipValsMap[zip]) {
          errors.push(`Duplicate value for Local Panel, Type 'Zip Code': ${zip}`);
        } else if (zip.length < 3) {
          errors.push(`Zip code length must be at least 3 digits. Invalid zip code: ${zip}`);
        } else {
          zipValsMap[zip] = true;
        }
      }
    }
    if (localTargets.dmas.length > 0) {
      for (const dma of localTargets.dmas.split(',')) {
        const trimmedDMA = dma.trim();

        if (dmaValsMap[trimmedDMA]) {
          errors.push(`Duplicate value for Local Panel, Type 'DMA Code': ${trimmedDMA}`);
        } else if (!Number.isInteger(Number(trimmedDMA)) || trimmedDMA.length !== 3) {
          errors.push(`Invalid DMA: ${trimmedDMA}`);
        } else {
          dmaValsMap[trimmedDMA] = true;
        }
      }
    }
  }

  return errors;
};
